import { useState, type FormEvent } from 'react';
import { Link } from 'wouter';
import {
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  Flame,
  Inbox,
  Send,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  useAcknowledgeReminder,
  useBriefing,
  useCapture,
  useCompleteTask,
  useDashboard,
  useDeleteAllBriefings,
  useDeleteAllInbox,
  useDeleteBriefing,
  useDiscardInbox,
  useMe,
  usePromoteInbox,
  useRegenerateBriefing,
  useTrackWorkspaceOpen,
  type Area,
} from '../lib/hooks';
import { useT } from '../lib/i18n';
import { ConfirmDeleteButton } from '../components/confirm-delete-button';

function greetingByHour(h: number): string {
  if (h < 5) return 'Khuya rồi';
  if (h < 11) return 'Chào buổi sáng';
  if (h < 14) return 'Chào buổi trưa';
  if (h < 18) return 'Chào buổi chiều';
  return 'Chào buổi tối';
}

function priorityAccent(p: string) {
  if (p === 'urgent')
    return { ring: 'ring-rose-400/40', dot: 'bg-rose-400', text: 'text-rose-300' };
  if (p === 'high')
    return { ring: 'ring-amber-400/40', dot: 'bg-amber-400', text: 'text-amber-300' };
  if (p === 'medium')
    return { ring: 'ring-indigo-400/30', dot: 'bg-indigo-400', text: 'text-indigo-300' };
  return {
    ring: 'ring-slate-400/20',
    dot: 'bg-slate-400',
    text: 'text-slate-700 dark:text-slate-300',
  };
}

export function DashboardPage() {
  const t = useT();
  const me = useMe();
  const dash = useDashboard();
  const capture = useCapture();
  const complete = useCompleteTask();
  const trackOpen = useTrackWorkspaceOpen();
  const promote = usePromoteInbox();
  const discard = useDiscardInbox();
  const deleteBriefing = useDeleteBriefing();
  const deleteAllBriefings = useDeleteAllBriefings();
  const deleteAllInbox = useDeleteAllInbox();
  const ackReminder = useAcknowledgeReminder();
  const briefing = useBriefing();
  const regenBriefing = useRegenerateBriefing();
  const [captureText, setCaptureText] = useState('');

  const onCapture = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = captureText.trim();
    if (!trimmed) return;
    await capture.mutateAsync({ content: trimmed });
    setCaptureText('');
    toast.success(t('capture.success'));
  };

  if (dash.isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-slate-500">
        {t('common.loading')}
      </div>
    );
  }

  const data = dash.data;
  if (!data) return null;

  const now = new Date();
  const greeting = greetingByHour(now.getHours());
  const firstName = (me.data?.name ?? '').split(' ').slice(-1)[0] ?? '';
  const dateStr = now.toLocaleDateString('vi-VN', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  const topAreas = data.areas.filter((a: Area) => !a.parentAreaId);
  const tasksByAreaCount = (id: string) => data.activeTasks.filter((tk) => tk.areaId === id).length;

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6 lg:p-8">
      {/* Hero */}
      <header className="space-y-1">
        <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
          <Clock className="h-3.5 w-3.5" />
          <span className="capitalize">{dateStr}</span>
          <span className="text-slate-400 dark:text-slate-600">·</span>
          <span>Berlin</span>
        </div>
        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
          <span className="text-slate-900 dark:text-slate-100">{greeting}</span>
          {firstName && <span className="gradient-text-brand">, {firstName}</span>}
        </h1>
        <p className="text-sm text-slate-500">
          Cockpit của chị —{' '}
          <span className="text-slate-700 dark:text-slate-300">
            {data.activeTasks.length} task đang mở
          </span>
          ,{' '}
          <span className="text-slate-700 dark:text-slate-300">
            {data.workspaces.length} workspace
          </span>
          ,{' '}
          <span className="text-slate-700 dark:text-slate-300">{data.inbox.length} inbox chờ</span>.
        </p>
      </header>

      {/* AI Briefing card */}
      <BriefingCard
        briefing={briefing.data ?? null}
        loading={briefing.isLoading}
        regenerating={regenBriefing.isPending}
        deleting={deleteBriefing.isPending}
        deletingAll={deleteAllBriefings.isPending}
        onDelete={() => {
          if (!briefing.data) return;
          deleteBriefing.mutate(briefing.data.id, {
            onSuccess: () => toast.success('Đã xoá briefing'),
            onError: (e) =>
              toast.error(e instanceof Error ? e.message : 'Không xoá được'),
          });
        }}
        onDeleteAll={() => {
          deleteAllBriefings.mutate(undefined, {
            onSuccess: () => toast.success('Đã xoá TẤT CẢ briefings cũ'),
            onError: (e) =>
              toast.error(e instanceof Error ? e.message : 'Không xoá được'),
          });
        }}
        onRegenerate={() => {
          regenBriefing.mutate(undefined, {
            onSuccess: () => toast.success('Đã generate briefing mới'),
            onError: (err: unknown) => {
              const m = err instanceof Error ? err.message : 'Lỗi';
              toast.error(`AI: ${m}`);
            },
          });
        }}
      />

      {/* Quick capture */}
      <form onSubmit={onCapture} className="panel">
        <div className="flex items-center gap-3 px-5 py-4">
          <Sparkles className="h-5 w-5 flex-shrink-0 text-indigo-300" />
          <input
            type="text"
            value={captureText}
            onChange={(e) => setCaptureText(e.target.value)}
            placeholder={t('capture.placeholder')}
            className="flex-1 bg-transparent text-base text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none"
            autoFocus
          />
          <button
            type="submit"
            disabled={capture.isPending || !captureText.trim()}
            className="btn-primary"
          >
            <Send className="h-4 w-4" />
            Capture
          </button>
        </div>
      </form>

      {/* Reminder banner */}
      {data.reminders.length > 0 && (
        <div className="rounded-2xl border border-amber-300 bg-gradient-to-r from-amber-50 to-rose-50 p-5 dark:border-amber-400/20 dark:from-amber-500/10 dark:to-rose-500/5 dark:backdrop-blur">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-200">
            <AlertTriangle className="h-4 w-4" />
            {data.reminders.length} nhắc nhở quan trọng
          </div>
          <ul className="space-y-3">
            {data.reminders.slice(0, 5).map((r) => (
              <li
                key={r.id}
                className="flex flex-col gap-2 rounded-xl border border-amber-200/60 bg-white/60 p-3 sm:flex-row sm:items-center sm:justify-between dark:border-amber-400/10 dark:bg-white/[0.03]"
              >
                <div className="flex items-start gap-2">
                  <span
                    className={`mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full ${
                      r.severity === 'critical'
                        ? 'bg-rose-500'
                        : r.severity === 'warning'
                          ? 'bg-amber-500'
                          : 'bg-cyan-500'
                    }`}
                  />
                  <div>
                    <div className="text-sm font-medium text-slate-900 dark:text-amber-100">
                      {r.title}
                    </div>
                    {r.description && (
                      <div className="mt-0.5 text-xs text-slate-600 dark:text-amber-100/70">
                        {r.description}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 sm:flex-shrink-0">
                  {r.links?.map((lnk, i) => (
                    <a
                      key={i}
                      href={lnk.url}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-secondary text-xs"
                    >
                      <ArrowUpRight className="h-3 w-3" />
                      {lnk.label}
                    </a>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      ackReminder.mutate(
                        { id: r.id },
                        {
                          onSuccess: () => toast.success('Đã snooze 24h'),
                        },
                      )
                    }
                    className="btn-ghost text-xs"
                    title="Snooze 24h"
                  >
                    Để sau
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Top hôm nay"
          value={data.top3Tasks.length}
          accent="from-rose-500/20 to-rose-500/5 border-rose-400/20"
          iconAccent="text-rose-300"
          icon={Flame}
        />
        <StatCard
          label="Đang mở"
          value={data.activeTasks.length}
          accent="from-indigo-500/20 to-indigo-500/5 border-indigo-400/20"
          iconAccent="text-indigo-300"
          icon={CheckCircle2}
        />
        <StatCard
          label="Inbox"
          value={data.inbox.length}
          accent="from-amber-500/20 to-amber-500/5 border-amber-400/20"
          iconAccent="text-amber-300"
          icon={Inbox}
        />
        <StatCard
          label="Areas"
          value={topAreas.length}
          accent="from-fuchsia-500/20 to-fuchsia-500/5 border-fuchsia-400/20"
          iconAccent="text-fuchsia-300"
          icon={TrendingUp}
        />
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* App Launcher — span 2 */}
        <section className="panel lg:col-span-2">
          <div className="panel-header">
            <h2 className="panel-title">App Launcher</h2>
            <Link
              href="/workspaces"
              className="flex items-center gap-1 text-xs font-medium text-slate-600 dark:text-slate-400 transition hover:text-white"
            >
              Quản lý
              <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="panel-body">
            {data.workspaces.length === 0 ? (
              <EmptyState
                title={t('dashboard.appLauncher.empty')}
                cta={{ href: '/workspaces', label: 'Thêm site đầu tiên' }}
              />
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {data.workspaces.map((w) => (
                  <a
                    key={w.id}
                    href={w.url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackOpen.mutate(w.id)}
                    className="group glass glass-hover relative overflow-hidden p-4"
                  >
                    <div
                      className="absolute inset-x-0 top-0 h-[2px]"
                      style={{ backgroundColor: w.color ?? '#6366f1' }}
                    />
                    <div
                      className="absolute -top-12 -right-12 h-24 w-24 rounded-full blur-3xl opacity-30 transition group-hover:opacity-60"
                      style={{ backgroundColor: w.color ?? '#6366f1' }}
                    />
                    <div className="relative">
                      <div className="mb-3 flex items-center justify-between">
                        <div
                          className="flex h-9 w-9 items-center justify-center rounded-xl text-white shadow-lg"
                          style={{
                            backgroundColor: w.color ?? '#6366f1',
                            boxShadow: `0 8px 20px -8px ${w.color ?? '#6366f1'}`,
                          }}
                        >
                          <span className="text-base font-bold">{w.name[0]}</span>
                        </div>
                        <ArrowUpRight className="h-4 w-4 text-slate-500 transition group-hover:text-white" />
                      </div>
                      <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {w.name}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-slate-500">
                        {w.url.replace(/^https?:\/\//, '')}
                      </div>
                      {w.openCount > 0 && (
                        <div className="mt-2 text-[10px] tracking-wider text-slate-400 dark:text-slate-600 uppercase">
                          {w.openCount} lần mở
                        </div>
                      )}
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Top 3 today */}
        <section className="panel">
          <div className="panel-header">
            <h2 className="panel-title flex items-center gap-2">
              <Flame className="h-3.5 w-3.5 text-rose-300" />
              Top 3 hôm nay
            </h2>
            <Link
              href="/tasks"
              className="flex items-center gap-1 text-xs font-medium text-slate-600 dark:text-slate-400 transition hover:text-white"
            >
              Tất cả
              <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="panel-body">
            {data.top3Tasks.length === 0 ? (
              <EmptyState title={t('dashboard.today.empty')} />
            ) : (
              <ol className="space-y-2.5">
                {data.top3Tasks.map((task, idx) => {
                  const ac = priorityAccent(task.priority);
                  return (
                    <li
                      key={task.id}
                      className={`group flex items-center gap-3 rounded-xl border border-slate-200 dark:border-white/5 bg-slate-50/60 dark:bg-white/[0.02] p-3 ring-1 ring-inset ${ac.ring} transition hover:bg-slate-100 dark:bg-white/[0.05]`}
                    >
                      <div
                        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${ac.dot} text-xs font-bold text-slate-900 shadow`}
                      >
                        {idx + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                          {task.title}
                        </div>
                        <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-500">
                          <span className={`uppercase ${ac.text}`}>{task.priority}</span>
                          {task.dueDate && (
                            <>
                              <span>·</span>
                              <span>hạn {new Date(task.dueDate).toLocaleDateString('vi')}</span>
                            </>
                          )}
                          {typeof task.score === 'number' && (
                            <>
                              <span>·</span>
                              <span
                                className="cursor-help text-indigo-600 dark:text-indigo-300"
                                title={task.scoreReasons?.join(' · ') ?? ''}
                              >
                                ⚡{task.score}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => complete.mutate(task.id)}
                        className="rounded-lg p-1.5 text-slate-500 opacity-0 transition group-hover:opacity-100 hover:bg-emerald-500/10 hover:text-emerald-300"
                        title="Đánh dấu xong"
                      >
                        <CheckCircle2 className="h-4 w-4" />
                      </button>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </section>

        {/* Areas — span 2 */}
        <section className="panel lg:col-span-2">
          <div className="panel-header">
            <h2 className="panel-title">Phân vùng (Areas)</h2>
            <Link
              href="/areas"
              className="flex items-center gap-1 text-xs font-medium text-slate-600 dark:text-slate-400 transition hover:text-white"
            >
              Quản lý
              <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="panel-body">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              {topAreas.map((area) => {
                const count = tasksByAreaCount(area.id);
                return (
                  <Link
                    key={area.id}
                    href={`/tasks?areaId=${area.id}`}
                    className="group relative overflow-hidden rounded-xl border border-slate-200 dark:border-white/5 bg-slate-50/60 dark:bg-white/[0.02] p-4 transition hover:border-slate-300 dark:border-white/15 hover:bg-slate-100 dark:bg-white/[0.05]"
                  >
                    <div
                      className="absolute -top-8 -right-8 h-20 w-20 rounded-full blur-2xl opacity-20 transition group-hover:opacity-50"
                      style={{ backgroundColor: area.color ?? '#64748b' }}
                    />
                    <div className="relative flex items-center justify-between">
                      <div>
                        <div className="mb-1 flex items-center gap-2">
                          <span
                            className="h-2 w-2 rounded-full"
                            style={{ backgroundColor: area.color ?? '#64748b' }}
                          />
                          <span className="text-[10px] tracking-wider text-slate-500 uppercase">
                            {area.type}
                          </span>
                        </div>
                        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                          {area.name}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="stat-number text-2xl">{count}</div>
                        <div className="text-[10px] tracking-wider text-slate-500 uppercase">
                          task
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Inbox */}
        <section className="panel">
          <div className="panel-header">
            <h2 className="panel-title flex items-center gap-2">
              <Inbox className="h-3.5 w-3.5 text-amber-300" />
              Inbox
            </h2>
            <div className="flex items-center gap-2">
              <span className="badge">{data.inbox.length}</span>
              {data.inbox.length > 0 && (
                <ConfirmDeleteButton
                  label={`Xoá cả ${data.inbox.length}`}
                  disabled={deleteAllInbox.isPending}
                  onConfirm={() => {
                    deleteAllInbox.mutate(undefined, {
                      onSuccess: () => toast.success('Đã xoá hết inbox'),
                      onError: (e) =>
                        toast.error(e instanceof Error ? e.message : 'Không xoá được'),
                    });
                  }}
                />
              )}
            </div>
          </div>
          <div className="panel-body">
            {data.inbox.length === 0 ? (
              <EmptyState title={t('dashboard.inbox.empty')} />
            ) : (
              <ul className="space-y-2">
                {data.inbox.slice(0, 6).map((item) => (
                  <li
                    key={item.id}
                    className="group rounded-xl border border-slate-200 dark:border-white/5 bg-slate-50/60 dark:bg-white/[0.02] p-3"
                  >
                    <div className="text-sm text-slate-800 dark:text-slate-200">
                      {item.rawContent}
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className="text-[10px] tracking-wider text-slate-500 uppercase">
                        {item.source}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => promote.mutate({ id: item.id, type: 'task' })}
                          className="text-xs font-medium text-indigo-500 transition hover:text-indigo-700 dark:text-indigo-300 dark:hover:text-white"
                        >
                          → Task
                        </button>
                        <ConfirmDeleteButton
                          label="Xoá inbox"
                          disabled={discard.isPending}
                          onConfirm={() => {
                            discard.mutate(item.id, {
                              onSuccess: () => toast.success('Đã xoá'),
                              onError: (e) =>
                                toast.error(
                                  e instanceof Error ? e.message : 'Không xoá được',
                                ),
                            });
                          }}
                        />
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      {/* Activity */}
      {data.recentActivity.length > 0 && (
        <section className="panel">
          <div className="panel-header">
            <h2 className="panel-title">Hoạt động gần đây</h2>
          </div>
          <div className="panel-body">
            <ul className="divide-y divide-slate-200 dark:divide-white/5">
              {data.recentActivity.slice(0, 8).map((a) => (
                <li
                  key={a.id}
                  className="flex items-center justify-between py-2.5 text-sm first:pt-0 last:pb-0"
                >
                  <span className="text-slate-700 dark:text-slate-300">
                    <code className="rounded bg-slate-100 dark:bg-white/5 px-1.5 py-0.5 font-mono text-[11px] text-indigo-300">
                      {a.action}
                    </code>
                  </span>
                  <span className="text-[11px] text-slate-500">
                    {new Date(a.createdAt).toLocaleString('vi')}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </div>
  );
}

function BriefingCard({
  briefing,
  loading,
  regenerating,
  deleting,
  deletingAll,
  onRegenerate,
  onDelete,
  onDeleteAll,
}: {
  briefing: import('../lib/hooks').Briefing | null;
  loading: boolean;
  regenerating: boolean;
  deleting: boolean;
  deletingAll: boolean;
  onRegenerate: () => void;
  onDelete: () => void;
  onDeleteAll: () => void;
}) {
  if (loading) return null;
  return (
    <section className="relative overflow-hidden rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-fuchsia-50 p-5 dark:border-indigo-400/20 dark:from-indigo-500/10 dark:via-transparent dark:to-fuchsia-500/10">
      <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-indigo-300/30 blur-3xl dark:bg-indigo-500/20" />
      <div className="relative">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-600 dark:text-indigo-300" />
            <span className="text-[11px] font-semibold tracking-[0.2em] text-indigo-700 uppercase dark:text-indigo-300">
              {briefing ? `Briefing — ${briefing.date}` : 'AI Briefing'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-secondary text-xs"
              onClick={onRegenerate}
              disabled={regenerating}
            >
              {regenerating ? '⏳' : '✨'} {briefing ? 'Refresh' : 'Generate'}
            </button>
            {briefing && (
              <ConfirmDeleteButton
                size="md"
                label="Xoá briefing này"
                disabled={deleting}
                onConfirm={onDelete}
              />
            )}
            <ConfirmDeleteButton
              size="md"
              label="Xoá HẾT briefings cũ"
              disabled={deletingAll}
              onConfirm={onDeleteAll}
            />
          </div>
        </div>
        {briefing ? (
          <p className="text-sm whitespace-pre-wrap text-slate-700 dark:text-slate-200">
            {briefing.content}
          </p>
        ) : (
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Chưa có briefing hôm nay. AI sẽ tự generate 7:00 sáng (Berlin), hoặc click{' '}
            <strong>Generate</strong> để tạo ngay.
          </p>
        )}
        {briefing?.deliveredTelegramAt && (
          <p className="mt-2 text-[11px] text-emerald-600 dark:text-emerald-400">
            ✓ Đã gửi Telegram lúc {new Date(briefing.deliveredTelegramAt).toLocaleTimeString('vi')}
          </p>
        )}
      </div>
    </section>
  );
}

function StatCard({
  label,
  value,
  accent,
  iconAccent,
  icon: Icon,
}: {
  label: string;
  value: number;
  accent: string;
  iconAccent: string;
  icon: typeof Flame;
}) {
  return (
    <div
      className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br p-5 backdrop-blur ${accent}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.2em] text-slate-600 dark:text-slate-400 uppercase">
            {label}
          </div>
          <div className="stat-number mt-2 text-4xl">{value}</div>
        </div>
        <Icon className={`h-5 w-5 ${iconAccent}`} />
      </div>
    </div>
  );
}

function EmptyState({ title, cta }: { title: string; cta?: { href: string; label: string } }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <p className="text-sm text-slate-500">{title}</p>
      {cta && (
        <Link href={cta.href} className="btn-primary mt-3">
          {cta.label}
        </Link>
      )}
    </div>
  );
}
